import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  CreditCard, Link2, Unlink, CheckCircle2, AlertTriangle, Loader2,
  Plus, ToggleLeft, ToggleRight, Trash2, Save, DollarSign, TrendingUp, Users, X,
  Ticket, KeyRound, Info,
} from "lucide-react";

function money(cents, currency = "usd") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "usd").toUpperCase(), maximumFractionDigits: 2 }).format((cents || 0) / 100);
}

/**
 * "Payments (Stripe)" card. Kicks off the Stripe Connect OAuth in a new tab
 * so the reseller doesn't lose their dashboard context. When Connect isn't
 * configured on the platform yet, the button is disabled and a clear hint
 * points at what needs to happen.
 */
export function StripeConnectCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setStatus(await api.resellerStripeStatus()); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.resellerStripeConnectLink();
      window.open(r.url, "_blank", "noopener,noreferrer");
      toast.info("Opening Stripe in a new tab — refresh this card after you finish.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Stripe? Your client-facing plans will stop accepting new subscriptions until you reconnect.")) return;
    setBusy(true);
    try { await api.resellerStripeDisconnect(); toast.success("Stripe disconnected."); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const connected = !!status?.connected;
  const connectEnabled = !!status?.connect_enabled;

  return (
    <div className="mt-6 rounded-3xl border border-[#E5DECF] bg-white p-6" data-testid="reseller-stripe-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CreditCard size={18} className="text-[#0D9488]" />
          <h2 className="font-heading text-lg font-extrabold text-[#1F2937]">Payments (Stripe)</h2>
        </div>
        {connected ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-[#10B981]/15 px-3 py-1 text-xs font-bold text-[#047857]" data-testid="stripe-connected-pill">
            <CheckCircle2 size={13} /> Connected
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-[#94A3B8]/15 px-3 py-1 text-xs font-bold text-[#6B7280]" data-testid="stripe-not-connected-pill">
            Not connected
          </span>
        )}
      </div>
      <p className="mt-1 font-body text-sm text-[#6B7280]">
        Connect your Stripe account so families that pick one of your plans pay <b>directly to you</b>. We never touch the money — ZoodLeap keeps 0%.
      </p>
      {loading ? (
        <div className="mt-6 flex justify-center"><Loader2 className="animate-spin text-[#0D9488]" /></div>
      ) : (
        <>
          {connected ? (
            <div className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl border border-[#E5DECF] bg-[#FAF7F2] p-4">
              <div className="flex-1 min-w-[220px]">
                <div className="font-body text-[10px] uppercase tracking-widest font-bold text-[#94A3B8]">Connected account</div>
                <code className="mt-0.5 block font-body text-sm font-bold text-[#1F2937]" data-testid="stripe-account-id">{status.account_id}</code>
              </div>
              <button onClick={disconnect} disabled={busy} data-testid="stripe-disconnect"
                className="inline-flex items-center gap-1.5 rounded-full border-2 border-[#E05A47] px-4 py-2 text-sm font-heading font-bold text-[#E05A47] hover:bg-[#E05A47] hover:text-white disabled:opacity-40">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Unlink size={14} />} Disconnect
              </button>
            </div>
          ) : connectEnabled ? (
            <button onClick={connect} disabled={busy} data-testid="stripe-connect-btn"
              className="mt-4 inline-flex items-center gap-2 rounded-2xl bg-[#635BFF] px-5 py-3 text-sm font-heading font-bold text-white shadow-sm transition-all hover:brightness-95 disabled:opacity-50">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />} Connect with Stripe
            </button>
          ) : (
            <div className="mt-4 flex items-start gap-2 rounded-2xl border-2 border-dashed border-[#F59E0B] bg-[#FEF3C7]/40 p-4 text-sm text-[#78350F]" data-testid="stripe-connect-disabled">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-heading font-bold">Stripe Connect isn't configured yet</div>
                <p className="mt-0.5 font-body">The platform admin hasn't turned on Stripe Connect (they need to set <code className="rounded bg-white px-1">STRIPE_CONNECT_CLIENT_ID</code>). Reach out to ZoodLeap support and we'll enable it for you — takes about 10 minutes.</p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const PLAN_INTERVAL_LABEL = { month: "monthly", year: "yearly" };

export function SeatsPlansExplainer() {
  return (
    <div className="mt-6 rounded-3xl border border-[#0D9488]/30 bg-gradient-to-br from-[#0D9488]/8 to-[#7A3FF2]/5 p-6" data-testid="seats-plans-explainer">
      <div className="flex items-center gap-2">
        <Info size={18} className="text-[#0D9488]" />
        <h2 className="font-heading text-lg font-extrabold text-[#1F2937]">How seats &amp; plans work</h2>
      </div>
      <p className="mt-1 font-body text-sm text-[#6B7280]">
        Two tools, one goal — get families and classrooms onto your branded ZoodLeap.
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded-2xl bg-white p-4 ring-1 ring-[#E5DECF]">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-[#0D9488]/12 text-[#0D9488]"><Ticket size={18} /></div>
            <div className="font-heading font-extrabold text-[#1F2937]">1 seat = 1 client account</div>
          </div>
          <p className="mt-2 font-body text-sm text-[#6B7280]">
            Your <b>seat pool</b> is how many <b>parent or teacher accounts</b> you can onboard — not how many children.
            A 10-seat pool can be 10 parents, 10 teachers, or any mix.
          </p>
          <ul className="mt-2 space-y-1 font-body text-xs text-[#6B7280]">
            <li>• <b>License codes</b> use seats directly — a redeemed code = 1 seat consumed.</li>
            <li>• <b>Plan checkouts</b> use seats when a family subscribes on your landing page.</li>
            <li>• Disable a client to instantly revoke access; delete them to free the seat.</li>
          </ul>
        </div>
        <div className="rounded-2xl bg-white p-4 ring-1 ring-[#E5DECF]">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-[#7A3FF2]/12 text-[#7A3FF2]"><Users size={18} /></div>
            <div className="font-heading font-extrabold text-[#1F2937]">Each plan sets its own kid cap</div>
          </div>
          <p className="mt-2 font-body text-sm text-[#6B7280]">
            When you create a plan, you set <b>how many children/students</b> that account can enroll. Match the cap to the audience.
          </p>
          <ul className="mt-2 space-y-1 font-body text-xs text-[#6B7280]">
            <li>• <b>Family plans</b> → try <b>5 children</b> per account (perfect for parents).</li>
            <li>• <b>Classroom plans</b> → try <b>30 students</b> per account (teacher-friendly).</li>
            <li>• You set the <b>price</b> — no upper limit. Charge whatever you'd like.</li>
          </ul>
        </div>
      </div>
      <div className="mt-3 rounded-2xl bg-white/70 p-3 text-xs font-body text-[#6B7280]">
        <b>Example:</b> A 10-seat pool + 2 plans (Family $19.99/mo with 5 kids, Classroom $49.99/mo with 30 kids).
        If 5 parents pick Family and 5 teachers pick Classroom, that's 10 seats used and up to <b>5×5 + 5×30 = 175 kids</b> learning on your platform.
        You earn <b>5×$19.99 + 5×$49.99 = ${(5*19.99 + 5*49.99).toFixed(2)}/mo</b> — every dollar routes to your Stripe.
      </div>
    </div>
  );
}

/**
 * "My subscription plans" card. Resellers build their own price sheet — each
 * plan surfaces on the branded /r/{slug} landing so families can subscribe
 * (Stripe Connect powers the checkout on the reseller's account).
 */
export function ResellerPlansCard({ stripeConnected }) {
  const [rows, setRows] = useState([]);
  const [analytics, setAnalytics] = useState({ plans: [], totals: { active_subscribers: 0, mrr_cents: 0, arr_cents: 0, churn_30d: 0 } });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", amount: "9.99", interval: "month", features: "", max_students: 30 });
  const [pending, setPending] = useState({});
  const [editing, setEditing] = useState(null); // plan being edited (features)
  const [editFeatures, setEditFeatures] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([api.listResellerPlans(), api.resellerPlansAnalytics()]);
      setRows(p.plans || []);
      setAnalytics(a);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const cents = Math.round(parseFloat(form.amount || "0") * 100);
    if (!form.name.trim() || cents < 100) { toast.error("Name required, price must be at least $1.00"); return; }
    setCreating(true);
    try {
      const features = form.features.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 6);
      const cap = Math.max(1, Math.min(500, parseInt(form.max_students, 10) || 30));
      await api.createResellerPlan({ name: form.name.trim(), description: form.description.trim() || null, amount_cents: cents, interval: form.interval, features, max_students: cap });
      toast.success("Plan added");
      setForm({ name: "", description: "", amount: "9.99", interval: "month", features: "", max_students: 30 });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  };

  const toggleActive = async (p) => {
    setPending((s) => ({ ...s, [p.id]: true }));
    try {
      await api.updateResellerPlan(p.id, { active: !p.active });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPending((s) => ({ ...s, [p.id]: false })); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete plan "${p.name}"? Existing subscribers stay subscribed but you can't sell it anymore.`)) return;
    setPending((s) => ({ ...s, [p.id]: true }));
    try { await api.deleteResellerPlan(p.id); toast.success("Plan removed"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setPending((s) => ({ ...s, [p.id]: false })); }
  };

  const openFeatureEditor = (p) => {
    setEditing(p);
    setEditFeatures((p.features || []).join("\n"));
  };
  const saveFeatures = async () => {
    if (!editing) return;
    const features = editFeatures.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 6);
    setPending((s) => ({ ...s, [editing.id]: true }));
    try {
      await api.updateResellerPlan(editing.id, { features });
      toast.success("Features saved");
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPending((s) => ({ ...s, [editing.id]: false })); }
  };

  const totals = analytics.totals || {};
  const maxMrr = Math.max(1, ...(analytics.plans || []).map((r) => r.mrr_cents));
  const analyticsByPlan = Object.fromEntries((analytics.plans || []).map((r) => [r.id, r]));

  return (
    <div className="mt-6 rounded-3xl border border-[#E5DECF] bg-white p-6" data-testid="reseller-plans-card">
      <div className="flex items-center gap-2">
        <DollarSign size={18} className="text-[#0D9488]" />
        <h2 className="font-heading text-lg font-extrabold text-[#1F2937]">My subscription plans</h2>
      </div>
      <p className="mt-1 font-body text-sm text-[#6B7280]">
        Create the price points you want families and teachers to see on your branded landing page. Money flows to <b>your</b> Stripe account.
      </p>

      {!stripeConnected && (
        <div className="mt-4 flex items-start gap-2 rounded-2xl border-2 border-dashed border-[#F59E0B] bg-[#FEF3C7]/50 p-3 text-xs font-body text-[#78350F]" data-testid="plans-need-stripe">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <div>You can build plans now, but they'll only accept subscriptions once your Stripe is connected above.</div>
        </div>
      )}

      <form onSubmit={submit} className="mt-4 rounded-2xl border border-[#E5DECF] bg-[#FAF7F2] p-4" data-testid="reseller-new-plan-form">
        <div className="grid gap-2 sm:grid-cols-[2fr_1fr_1fr_1fr_auto]">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Plan name — e.g. Family Monthly" data-testid="plan-name-input"
            className="rounded-xl border-2 border-[#E5DECF] bg-white px-3 py-2 font-body text-sm outline-none focus:border-[#0D9488]" />
          <input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} type="number" step="0.01" min="1" placeholder="Amount" data-testid="plan-amount-input"
            className="rounded-xl border-2 border-[#E5DECF] bg-white px-3 py-2 font-body text-sm outline-none focus:border-[#0D9488]" />
          <select value={form.interval} onChange={(e) => setForm({ ...form, interval: e.target.value })} data-testid="plan-interval-input"
            className="rounded-xl border-2 border-[#E5DECF] bg-white px-3 py-2 font-body text-sm outline-none focus:border-[#0D9488]">
            <option value="month">/month</option>
            <option value="year">/year</option>
          </select>
          <input value={form.max_students} onChange={(e) => setForm({ ...form, max_students: e.target.value })} type="number" min="1" max="500" placeholder="Kids" data-testid="plan-max-students-input"
            title="Max children/students per account on this plan"
            className="rounded-xl border-2 border-[#E5DECF] bg-white px-3 py-2 font-body text-sm outline-none focus:border-[#0D9488]" />
          <button type="submit" disabled={creating} data-testid="plan-create-btn"
            className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-[#0D9488] px-4 py-2 text-sm font-heading font-bold text-white hover:brightness-95 disabled:opacity-50">
            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add
          </button>
        </div>
        <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_auto]">
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short description (optional)" data-testid="plan-desc-input"
            className="rounded-xl border-2 border-[#E5DECF] bg-white px-3 py-2 font-body text-sm outline-none focus:border-[#0D9488]" />
          <div className="flex items-center gap-2 px-3 font-body text-[11px] text-[#6B7280]">
            <span>Suggested:</span>
            <button type="button" onClick={() => setForm({ ...form, max_students: 5 })} className="rounded-full border border-[#E5DECF] px-2 py-0.5 font-bold hover:border-[#0D9488] hover:text-[#0D9488]">Family · 5</button>
            <button type="button" onClick={() => setForm({ ...form, max_students: 30 })} className="rounded-full border border-[#E5DECF] px-2 py-0.5 font-bold hover:border-[#0D9488] hover:text-[#0D9488]">Classroom · 30</button>
          </div>
        </div>
        <textarea value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} data-testid="plan-features-input"
          placeholder={"Feature bullets (optional, one per line, up to 6)\nFull PreK–8 curriculum\nProgress reports & stickers\nUp to 5 children"}
          className="mt-2 h-24 w-full resize-none rounded-xl border-2 border-[#E5DECF] bg-white px-3 py-2 font-body text-xs outline-none focus:border-[#0D9488]" />
      </form>

      {rows.length > 0 && (
        <div className="mt-4 grid gap-2 sm:grid-cols-3" data-testid="reseller-plan-analytics-stats">
          <div className="rounded-2xl border border-[#E5DECF] bg-[#FAF7F2] px-4 py-3">
            <div className="font-body text-[10px] uppercase tracking-widest font-semibold text-[#6B7280]">Active subscribers</div>
            <div className="mt-0.5 flex items-center gap-1.5 font-heading text-2xl font-extrabold text-[#1F2937]">
              <Users size={18} className="text-[#0D9488]" /> <span data-testid="analytics-active">{totals.active_subscribers ?? 0}</span>
            </div>
          </div>
          <div className="rounded-2xl border border-[#E5DECF] bg-[#FAF7F2] px-4 py-3">
            <div className="font-body text-[10px] uppercase tracking-widest font-semibold text-[#6B7280]">MRR / ARR</div>
            <div className="mt-0.5 font-heading text-2xl font-extrabold text-[#047857]" data-testid="analytics-mrr">{money(totals.mrr_cents || 0)}</div>
            <div className="font-body text-[11px] text-[#6B7280]">{money(totals.arr_cents || 0)}/yr projected</div>
          </div>
          <div className="rounded-2xl border border-[#E5DECF] bg-[#FAF7F2] px-4 py-3">
            <div className="font-body text-[10px] uppercase tracking-widest font-semibold text-[#6B7280]">Churn (30d)</div>
            <div className="mt-0.5 flex items-center gap-1.5 font-heading text-2xl font-extrabold text-[#E05A47]">
              <TrendingUp size={18} className="text-[#E05A47] rotate-180" /> <span data-testid="analytics-churn">{totals.churn_30d ?? 0}</span>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="mt-6 flex justify-center"><Loader2 className="animate-spin text-[#0D9488]" /></div>
      ) : rows.length === 0 ? (
        <div className="mt-4 rounded-2xl border border-dashed border-[#E5DECF] bg-[#FAF7F2] p-6 text-center" data-testid="reseller-plans-empty">
          <DollarSign size={22} className="mx-auto text-[#94A3B8]" />
          <p className="mt-2 font-body text-sm text-[#6B7280]">No plans yet — build your first price point above.</p>
        </div>
      ) : (
        <div className="mt-4 space-y-2" data-testid="reseller-plans-list">
          {rows.map((p) => {
            const stats = analyticsByPlan[p.id] || { subscribers: 0, mrr_cents: 0, churn_30d: 0 };
            const mrrPct = maxMrr > 0 ? Math.max(3, Math.round((stats.mrr_cents / maxMrr) * 100)) : 0;
            return (
              <div key={p.id} data-testid={`plan-row-${p.id}`}
                className={`rounded-2xl border p-4 ${p.active ? "border-[#E5DECF] bg-white" : "border-[#E5DECF] bg-[#FAF7F2] opacity-70"}`}>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1.5fr_auto_auto_auto_auto] sm:items-center">
                  <div>
                    <div className="font-heading text-sm font-extrabold text-[#1F2937]" data-testid={`plan-name-${p.id}`}>{p.name}</div>
                    {p.description && <div className="font-body text-xs text-[#6B7280]">{p.description}</div>}
                    {(p.features || []).length > 0 && (
                      <div className="mt-1 font-body text-[11px] text-[#6B7280]" data-testid={`plan-features-preview-${p.id}`}>
                        {(p.features || []).slice(0, 3).map((f, i) => <span key={i} className="mr-2">• {f}</span>)}
                        {(p.features || []).length > 3 && <span>+{(p.features || []).length - 3} more</span>}
                      </div>
                    )}
                  </div>
                  <div className="font-heading font-bold text-[#1F2937]" data-testid={`plan-price-${p.id}`}>
                    {money(p.amount_cents, p.currency)}<span className="ml-0.5 font-body text-xs text-[#6B7280]">/{p.interval === "year" ? "yr" : "mo"}</span>
                    <div className="font-body text-[10px] font-semibold text-[#94A3B8]">up to {p.max_students || 30} kids</div>
                  </div>
                  <button onClick={() => openFeatureEditor(p)} data-testid={`plan-edit-features-${p.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full border-2 border-[#E5DECF] px-3 py-1.5 text-xs font-bold text-[#6B7280] hover:border-[#0D9488] hover:text-[#0D9488]">
                    Features · {(p.features || []).length}
                  </button>
                  <button onClick={() => toggleActive(p)} disabled={pending[p.id]} data-testid={`plan-toggle-${p.id}`}
                    title={p.active ? "Hide from landing page" : "Show on landing page"}
                    className="inline-flex items-center gap-1.5 rounded-full border-2 border-[#E5DECF] px-3 py-1.5 text-xs font-bold text-[#6B7280] hover:border-[#0D9488] hover:text-[#0D9488]">
                    {pending[p.id] ? <Loader2 size={12} className="animate-spin" /> : (p.active ? <ToggleRight size={14} className="text-[#0D9488]" /> : <ToggleLeft size={14} />)}
                    {p.active ? "Active" : "Inactive"}
                  </button>
                  <button onClick={() => remove(p)} disabled={pending[p.id]} data-testid={`plan-delete-${p.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full border-2 border-[#E05A47] px-3 py-1.5 text-xs font-heading font-bold text-[#E05A47] hover:bg-[#E05A47] hover:text-white disabled:opacity-40">
                    {pending[p.id] ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Delete
                  </button>
                </div>
                {p.active && (
                  <div className="mt-2 flex items-center gap-3 text-xs font-body text-[#6B7280]" data-testid={`plan-analytics-${p.id}`}>
                    <span><b className="text-[#1F2937]">{stats.subscribers}</b> subscriber{stats.subscribers === 1 ? "" : "s"}</span>
                    <span>· <b className="text-[#047857]">{money(stats.mrr_cents)}</b>/mo</span>
                    {stats.churn_30d > 0 && <span>· <b className="text-[#E05A47]">{stats.churn_30d}</b> churned (30d)</span>}
                    <div className="ml-auto h-1.5 flex-1 max-w-[200px] rounded-full bg-[#F2ECE1]">
                      <div className="h-full rounded-full bg-[#0D9488]" style={{ width: `${mrrPct}%` }} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditing(null)} data-testid="features-editor-backdrop">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="features-editor">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg font-extrabold text-[#1F2937]">Feature bullets · <span className="text-[#0D9488]">{editing.name}</span></h3>
              <button onClick={() => setEditing(null)} data-testid="features-editor-close" className="rounded-full p-1 text-[#6B7280] hover:bg-[#F2ECE1]"><X size={18} /></button>
            </div>
            <p className="mt-1 font-body text-sm text-[#6B7280]">One per line — up to 6. These show on your <b>/r/{"{slug}"}</b> landing page under this plan's price.</p>
            <textarea value={editFeatures} onChange={(e) => setEditFeatures(e.target.value)} data-testid="features-editor-textarea"
              placeholder="Full PreK–8 curriculum&#10;Progress reports & stickers&#10;Up to 3 children"
              className="mt-3 h-40 w-full resize-none rounded-2xl border-2 border-[#E5DECF] bg-[#FAF7F2] px-3 py-2 font-body text-sm outline-none focus:border-[#0D9488]" />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} data-testid="features-editor-cancel" className="rounded-full border-2 border-[#E5DECF] px-4 py-2 text-sm font-bold text-[#6B7280] hover:border-[#0D9488] hover:text-[#0D9488]">Cancel</button>
              <button onClick={saveFeatures} disabled={pending[editing.id]} data-testid="features-editor-save" className="inline-flex items-center gap-1.5 rounded-full bg-[#0D9488] px-4 py-2 text-sm font-heading font-bold text-white hover:brightness-95 disabled:opacity-50">
                {pending[editing.id] ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
